/**
 * AddFacilityForm Component
 * Allows users to submit new facility locations with photos
 * Supports offline mode with IndexedDB queue
 */

import { useState, useRef, useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, IconButton, Alert, Autocomplete,
  Slide, Stack, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { compressImage, formatFileSize } from '../services/imageCompression';
import { offlineQueue } from '../services/offlineQueue';
import { communityFacilitiesService } from '../services/communityFacilities';
import { CyberSpinner } from './CyberSpinner';
import { telegram } from '../services/telegram';
import { NEW_MEXICO_COUNTIES } from '../types';
import { useAppStore } from '../stores/appStore';
import { getCyberColors } from '../theme/cyberColors';
import { useAutoDismiss } from '../hooks/useAutoDismiss';

// Extended theme colors for form components
const getFormColors = (darkMode: boolean, highContrast: boolean) => {
  const cyber = getCyberColors(darkMode, highContrast);
  return {
    ...cyber,
    // Form-specific extensions
    bgInput: cyber.bgSecondary,
    warningBg: darkMode 
      ? (highContrast ? 'rgba(254, 240, 138, 0.2)' : 'rgba(251, 191, 36, 0.15)') 
      : (highContrast ? 'rgba(113, 63, 18, 0.15)' : 'rgba(245, 158, 11, 0.15)'),
    warningText: cyber.neonYellow,
    successBg: darkMode 
      ? (highContrast ? 'rgba(74, 222, 128, 0.15)' : 'rgba(16, 185, 129, 0.1)') 
      : (highContrast ? 'rgba(20, 83, 45, 0.15)' : 'rgba(16, 185, 129, 0.15)'),
    successText: cyber.neonGreen,
    infoBg: darkMode 
      ? (highContrast ? 'rgba(96, 165, 250, 0.15)' : 'rgba(59, 130, 246, 0.1)') 
      : (highContrast ? 'rgba(30, 58, 138, 0.15)' : 'rgba(59, 130, 246, 0.15)'),
    infoText: cyber.neonBlue,
    errorBg: darkMode 
      ? (highContrast ? 'rgba(248, 113, 113, 0.15)' : 'rgba(239, 68, 68, 0.1)') 
      : (highContrast ? 'rgba(127, 29, 29, 0.15)' : 'rgba(239, 68, 68, 0.15)'),
    borderFocus: cyber.neonBlue,
  };
};

// Facility types - complete list matching all facility buttons
const FACILITY_TYPES = [
  // Primary Types
  { value: 'WL', label: 'Well' },
  { value: 'SWD', label: 'SWD (Saltwater Disposal)' },
  { value: 'RIG', label: 'Rig' },
  { value: 'FRK', label: 'Fracking Site' },
  { value: 'DRL', label: 'Drilling Site' },
  // Surface Facilities
  { value: 'WP', label: 'Well Pad' },
  { value: 'WH', label: 'Well Head' },
  { value: 'TB', label: 'Tank Battery' },
  { value: 'FS', label: 'Flare Stack' },
  { value: 'CS', label: 'Compressor Station' },
  { value: 'GP', label: 'Gas Plant' },
  { value: 'GGS', label: 'Gas Gathering System' },
  { value: 'RF', label: 'Refinery' },
  { value: 'TP', label: 'Treating Plant' },
  { value: 'INJ', label: 'Injection Plant' },
  // Pipelines
  { value: 'PLG', label: 'Pipeline - Gas' },
  { value: 'PLC', label: 'Pipeline - Crude' },
  { value: 'PLW', label: 'Pipeline - Water' },
  // Storage & Equipment
  { value: 'GS', label: 'Gas Storage' },
  { value: 'BGT', label: 'Below Grade Tank' },
  { value: 'MGS', label: 'Metering Station' },
  { value: 'CP', label: 'Crude Pump' },
  { value: 'PMP', label: 'Pump Jack' },
  // Waste & Environmental
  { value: 'PIT', label: 'Pits' },
  { value: 'WM', label: 'Waste Management' },
  { value: 'RFL', label: 'Recycling Facility' },
  { value: 'BW', label: 'Brine Well' },
  { value: 'RM', label: 'Remediation Site' },
  { value: 'AB', label: 'Abatement Site' },
  // Other
  { value: 'SC', label: 'Service Company' },
  { value: 'CI', label: 'Class I/II Injection' },
  { value: 'OTH', label: 'Other' },
  // Food Vendors - requires business verification
  { value: 'TACO', label: '🌮 Taco Truck' },
  { value: 'FOOD', label: '🍔 Food Truck' },
  { value: 'CAFE', label: '☕ Coffee/Cafe' }
];

// Food vendor facility types that require business verification
const FOOD_VENDOR_TYPES = ['TACO', 'FOOD', 'CAFE'];

interface AddFacilityFormProps {
  open: boolean;
  onClose: () => void;
  latitude: number;
  longitude: number;
  onSuccess?: () => void;
  onViewExistingFacility?: (facility: { api: string; name: string }) => void;
  onViewOnMap?: (lat: number, lng: number, name: string) => void;
  onAddToFavorites?: (facility: { id: string; name: string; facilityType: string; latitude: number; longitude: number; operator?: string; county?: string; api?: string }) => void;
  onOpenCommunityFacilities?: () => void;
}

interface PhotoPreview {
  original: File;
  compressed: {
    base64: string;
    size: number;
  } | null;
  isCompressing: boolean;
}

interface SubmittedFacility {
  id: string;
  name: string;
  facilityType: string;
  latitude: number;
  longitude: number;
  operator?: string;
  county?: string;
}

export function AddFacilityForm({ open, onClose, latitude, longitude, onSuccess, onViewExistingFacility, onViewOnMap, onAddToFavorites, onOpenCommunityFacilities }: AddFacilityFormProps) {
  const darkMode = useAppStore((state) => state.darkMode);
  const highContrast = useAppStore((state) => state.highContrast);
  const colors = getFormColors(darkMode, highContrast);
  
  const [facilityType, setFacilityType] = useState('');
  const [name, setName] = useState('');
  const [apiNumber, setApiNumber] = useState('');
  const [operator, setOperator] = useState('');
  const [county, setCounty] = useState('');
  const [notes, setNotes] = useState('');
  const [operatorsList, setOperatorsList] = useState<string[]>([]);
  const [isLoadingOperators, setIsLoadingOperators] = useState(false);
  
  // Food vendor specific fields
  const [businessLicense, setBusinessLicense] = useState('');
  const [ein, setEin] = useState('');
  const [foodPermit, setFoodPermit] = useState('');
  const [healthPermit, setHealthPermit] = useState('');
  const [operatingHours, setOperatingHours] = useState('');
  const [menuSpecialty, setMenuSpecialty] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [acceptsCards, setAcceptsCards] = useState(true);
  const [acceptsCash, setAcceptsCash] = useState(true);
  
  // Check if selected type is a food vendor
  const isFoodVendor = FOOD_VENDOR_TYPES.includes(facilityType);
  const [photo, setPhoto] = useState<PhotoPreview | null>(null);
  
  // Menu photos for food vendors (up to 10)
  const [menuPhotos, setMenuPhotos] = useState<PhotoPreview[]>([]);
  const menuInputRef = useRef<HTMLInputElement>(null);
  const MAX_MENU_PHOTOS = 10;
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'queued' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  // API existence check state
  const [existingFacility, setExistingFacility] = useState<{ api: string; name: string; operator?: string } | null>(null);
  const [isCheckingApi, setIsCheckingApi] = useState(false);

  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [lastSubmittedFacility, setLastSubmittedFacility] = useState<SubmittedFacility | null>(null);
  
  // Sync status state
  const [pendingCount, setPendingCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'error'>('idle');
  const [lastSyncError, setLastSyncError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const isOnline = navigator.onLine;

  useAutoDismiss(errorMessage, () => setErrorMessage(''));
  useAutoDismiss(lastSyncError, () => setLastSyncError(null));

  // Subscribe to pending count and sync status changes
  useEffect(() => {
    // Get initial pending count
    offlineQueue.getPendingCount().then(count => {
      setPendingCount(count);
      console.log(`📊 Initial pending submissions: ${count}`);
    });

    // Subscribe to changes
    const unsubscribe = offlineQueue.onPendingCountChange((count) => {
      console.log(`📊 Pending count changed: ${count}`);
      setPendingCount(count);
    });

    return () => unsubscribe();
  }, []);

  // Fetch operators list lazily when needed (not on dialog open)
  // This prevents slow dialog opening
  const fetchOperators = async () => {
    if (operatorsList.length > 0) return; // Already loaded
    
    setIsLoadingOperators(true);
    try {
      const response = await fetch('/api/operators/list');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.operators) {
          setOperatorsList(data.operators);
        }
      }
    } catch (error) {
      console.error('Failed to fetch operators:', error);
    } finally {
      setIsLoadingOperators(false);
    }
  };

  // Debounced API number existence check
  useEffect(() => {
    // Clear existing facility when API changes
    setExistingFacility(null);
    
    // Only check if we have a substantial API number (at least 5 chars)
    const trimmedApi = apiNumber.trim();
    if (trimmedApi.length < 5) {
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setIsCheckingApi(true);
      try {
        // Search for the API number in the database
        const response = await fetch(`/api/facilities/search?query=${encodeURIComponent(trimmedApi)}&type=all&limit=5`);
        if (response.ok) {
          const data = await response.json();
          // Check if any facility has a matching API/identifier
          const facilities = data.facilities || [];
          const match = facilities.find((f: any) => {
            const facilityApi = (f.api || f.identifier || f.apiNumber || '').toLowerCase();
            return facilityApi.includes(trimmedApi.toLowerCase()) || trimmedApi.toLowerCase().includes(facilityApi);
          });
          
          if (match) {
            setExistingFacility({
              api: match.api || match.identifier || match.apiNumber || '',
              name: match.wellName || match.name || 'Unknown',
              operator: match.operator
            });
          }
        }
      } catch (error) {
        console.error('API check failed:', error);
      } finally {
        setIsCheckingApi(false);
      }
    }, 400); // 400ms debounce

    return () => clearTimeout(debounceTimer);
  }, [apiNumber]);

  const handlePhotoSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPhoto({
      original: file,
      compressed: null,
      isCompressing: true
    });

    try {
      const result = await compressImage(file, {
        maxWidth: 1200,
        maxHeight: 1200,
        quality: 0.8
      });

      setPhoto({
        original: file,
        compressed: {
          base64: result.base64,
          size: result.compressedSize
        },
        isCompressing: false
      });

      console.log(`📸 Photo compressed: ${formatFileSize(result.originalSize)} → ${formatFileSize(result.compressedSize)} (${result.compressionRatio}% reduction)`);
    } catch (error) {
      console.error('Failed to compress photo:', error);
      setPhoto(null);
      setErrorMessage('Failed to process photo. Please try again.');
    }
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    telegram.hapticFeedback('light');
    // Validate required fields
    if (!facilityType) {
      telegram.hapticFeedback('error');
      setErrorMessage('Please select a facility type.');
      return;
    }
    if (!name.trim()) {
      telegram.hapticFeedback('error');
      setErrorMessage('Please enter a facility name.');
      return;
    }
    // API number not required for food vendors
    if (!isFoodVendor && !apiNumber.trim()) {
      telegram.hapticFeedback('error');
      setErrorMessage('Please enter an API number.');
      return;
    }
    // Operator required for non-food vendors only
    if (!isFoodVendor && !operator.trim()) {
      telegram.hapticFeedback('error');
      setErrorMessage('Please enter an operator name.');
      return;
    }
    if (!county.trim()) {
      telegram.hapticFeedback('error');
      setErrorMessage('Please enter a county.');
      return;
    }
    
    // Food vendor specific validation
    if (isFoodVendor) {
      // Require location photo for food vendors
      if (!photo?.compressed?.base64) {
        telegram.hapticFeedback('error');
        setErrorMessage('Please take a photo of your location so customers can find you!');
        return;
      }
      if (!businessLicense.trim()) {
        telegram.hapticFeedback('error');
        setErrorMessage('Please enter your Business License Number.');
        return;
      }
      if (!foodPermit.trim()) {
        telegram.hapticFeedback('error');
        setErrorMessage('Please enter your Food Handler\'s Permit number.');
        return;
      }
      if (!menuSpecialty.trim()) {
        telegram.hapticFeedback('error');
        setErrorMessage('Please describe your menu specialty.');
        return;
      }
      if (!operatingHours.trim()) {
        telegram.hapticFeedback('error');
        setErrorMessage('Please enter your operating hours.');
        return;
      }
      if (!contactPhone.trim()) {
        telegram.hapticFeedback('error');
        setErrorMessage('Please enter a contact phone number.');
        return;
      }
    }
    
    // Warn if API already exists (only for non-food vendors)
    if (!isFoodVendor && existingFacility) {
      telegram.hapticFeedback('warning');
      setErrorMessage(`This API number already exists: "${existingFacility.name}". Click the link below to view it.`);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSyncStatus('syncing');
    setLastSyncError(null);

    try {
      // Build food vendor metadata if applicable
      const foodVendorInfo = isFoodVendor ? {
        businessLicense: businessLicense.trim(),
        ein: ein.trim() || null,
        foodPermit: foodPermit.trim(),
        healthPermit: healthPermit.trim() || null,
        menuSpecialty: menuSpecialty.trim(),
        operatingHours: operatingHours.trim(),
        contactPhone: contactPhone.trim(),
        acceptsCash,
        acceptsCards,
        menuPhotoCount: menuPhotos.filter(p => p.compressed?.base64).length,
      } : null;
      
      console.log('🚀 Submitting facility:', {
        facilityType,
        name: name.trim(),
        apiNumber: apiNumber.trim(),
        operator: operator.trim(),
        latitude,
        longitude,
        county: county.trim(),
        hasPhoto: !!photo?.compressed?.base64,
        isFoodVendor,
        menuPhotoCount: menuPhotos.length,
        ...(isFoodVendor && { foodVendorInfo })
      });
      
      // Build notes with food vendor info if applicable
      const combinedNotes = isFoodVendor 
        ? [
            notes.trim(),
            `🍽️ Menu: ${menuSpecialty.trim()}`,
            `⏰ Hours: ${operatingHours.trim()}`,
            `📞 Phone: ${contactPhone.trim()}`,
            `💵 Cash: ${acceptsCash ? 'Yes' : 'No'} | 💳 Card: ${acceptsCards ? 'Yes' : 'No'}`,
            `📋 License: ${businessLicense.trim()}`,
            foodPermit.trim() ? `🍴 Food Permit: ${foodPermit.trim()}` : null,
            healthPermit.trim() ? `🏥 Health Permit: ${healthPermit.trim()}` : null,
            ein.trim() ? `📄 EIN: ${ein.trim()}` : null,
            menuPhotos.length > 0 ? `📸 Menu Photos: ${menuPhotos.length}` : null,
          ].filter(Boolean).join('\n')
        : notes.trim();
      
      // Queue submission for offline sync - returns the submission ID
      const submissionId = await offlineQueue.queueSubmission({
        facilityType,
        name: name.trim(),
        apiNumber: isFoodVendor ? `FOOD-${Date.now()}` : (apiNumber.trim() || null), // Generate unique ID for food vendors
        operator: isFoodVendor ? name.trim() : (operator.trim() || null), // Use truck name as operator for food vendors
        latitude,
        longitude,
        county: county.trim() || null,
        notes: combinedNotes || null,
        photoBase64: photo?.compressed?.base64 || null,
        photoSize: photo?.compressed?.size || 0
      });
      
      console.log('✅ Submission queued with ID:', submissionId);

      telegram.hapticFeedback('success');
      
      // Store the submitted facility info for sharing
      const newFacility = {
        id: submissionId,
        name: name.trim(),
        facilityType,
        latitude,
        longitude,
        operator: operator.trim() || undefined,
        county: county.trim() || undefined,
        api: apiNumber.trim() || undefined
      };
      
      setLastSubmittedFacility(newFacility);
      
      // Add to favorites automatically
      if (onAddToFavorites) {
        onAddToFavorites(newFacility);
      }
      
      // Immediately add optimistic marker to community facilities
      // This makes the facility visible to all users instantly (before server confirmation)
      communityFacilitiesService.addOptimistic({
        id: submissionId,
        telegram_user_id: telegram.getUserId() || 'unknown',
        facility_type: facilityType,
        name: name.trim(),
        api_number: apiNumber.trim() || undefined,
        operator: operator.trim() || undefined,
        latitude,
        longitude,
        county: county.trim() || undefined,
        notes: notes.trim() || undefined,
        photo_url: undefined,
        status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      
      if (navigator.onLine) {
        setSubmitStatus('success');
        setSyncStatus('idle');
        console.log('📡 Online - sync should happen automatically');
        // Show share dialog after a brief delay
        setTimeout(() => {
          setShowShareDialog(true);
        }, 500);
        
        // Check sync status after a moment
        setTimeout(async () => {
          const pending = await offlineQueue.getPendingCount();
          console.log(`📊 After sync attempt - pending: ${pending}`);
          if (pending > 0) {
            setSyncStatus('error');
            setLastSyncError('Sync may have failed - check network');
          }
        }, 3000);
      } else {
        setSubmitStatus('queued');
        setSyncStatus('idle');
        console.log('📴 Offline - queued for later sync');
        // Still show share dialog for offline - will sync later
        setTimeout(() => {
          setShowShareDialog(true);
        }, 500);
      }

    } catch (error) {
      console.error('❌ Submission failed:', error);
      setSubmitStatus('error');
      setSyncStatus('error');
      const errMsg = error instanceof Error ? error.message : 'Unknown error';
      setLastSyncError(errMsg);
      setErrorMessage(`Failed to save submission: ${errMsg}`);
      telegram.hapticFeedback('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFacilityType('');
    setName('');
    setApiNumber('');
    setOperator('');
    setCounty('');
    setNotes('');
    setPhoto(null);
    setSubmitStatus('idle');
    setErrorMessage('');
    setExistingFacility(null);
    setIsCheckingApi(false);
    // Reset food vendor fields
    setBusinessLicense('');
    setEin('');
    setFoodPermit('');
    setHealthPermit('');
    setOperatingHours('');
    setMenuSpecialty('');
    setContactPhone('');
    setAcceptsCards(true);
    setAcceptsCash(true);
    // Reset menu photos
    setMenuPhotos([]);
  };

  // Share functionality
  const generateShareLink = (facilityId: string) => {
    return `https://ziawellnav.pages.dev/?facility=${facilityId}`;
  };

  const handleShare = async () => {
    if (!lastSubmittedFacility) return;
    
    const shareLink = generateShareLink(lastSubmittedFacility.id);
    const facilityLabel = FACILITY_TYPES.find(f => f.value === lastSubmittedFacility.facilityType)?.label || 'Facility';
    const shareText = `📍 ${facilityLabel}: ${lastSubmittedFacility.name}\n${lastSubmittedFacility.operator ? `Operator: ${lastSubmittedFacility.operator}\n` : ''}${lastSubmittedFacility.county ? `County: ${lastSubmittedFacility.county}\n` : ''}`;
    
    try {
      // Check if we're in Telegram WebApp
      const tgWebApp = (window as any).Telegram?.WebApp;
      if (tgWebApp) {
        // Close our dialogs first so Telegram's share UI isn't blocked
        setShowShareDialog(false);
        onClose();
        
        // Small delay to let dialogs close before opening Telegram share
        setTimeout(() => {
          // Use Telegram's native share URL
          const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(shareText)}`;
          if (tgWebApp.openTelegramLink) {
            tgWebApp.openTelegramLink(telegramShareUrl);
          } else {
            window.open(telegramShareUrl, '_blank');
          }
          telegram.hapticFeedback('success');
          
          // Clean up state after sharing
          setLastSubmittedFacility(null);
          resetForm();
          onSuccess?.();
        }, 100);
      } else if (navigator.share) {
        // Fallback to native share API for non-Telegram browsers
        await navigator.share({
          title: `${lastSubmittedFacility.name} - ZiaWellNav`,
          text: shareText,
          url: shareLink
        });
      } else {
        // Final fallback: copy to clipboard
        await navigator.clipboard.writeText(shareText + shareLink);
        telegram.hapticFeedback('success');
      }
    } catch (error) {
      console.log('Share cancelled or failed:', error);
    }
  };

  const handleCopyLink = async () => {
    if (!lastSubmittedFacility) return;
    try {
      await navigator.clipboard.writeText(generateShareLink(lastSubmittedFacility.id));
      telegram.hapticFeedback('success');
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const closeShareDialog = () => {
    setShowShareDialog(false);
    setLastSubmittedFacility(null);
    resetForm();
    onSuccess?.();
    onClose();
  };

  // Theme-aware button styles
  const primaryButtonSx = {
    py: 1.5,
    background: darkMode 
      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(37, 99, 235, 0.9) 100%)'
      : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    backdropFilter: 'blur(12px)',
    border: `1px solid ${colors.borderHover}`,
    color: 'white',
    fontWeight: 600,
    borderRadius: 3,
    textTransform: 'none' as const,
    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    '&:hover': {
      background: darkMode
        ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(37, 99, 235, 1) 100%)'
        : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 30px rgba(59, 130, 246, 0.5)'
    },
    '&:disabled': {
      background: darkMode ? 'rgba(100, 116, 139, 0.5)' : 'rgba(148, 163, 184, 0.5)',
      color: 'rgba(255, 255, 255, 0.5)'
    }
  };

  const secondaryButtonSx = {
    py: 1,
    background: colors.bgInput,
    backdropFilter: 'blur(12px)',
    border: `1px solid ${colors.borderDefault}`,
    color: colors.textSecondary,
    fontWeight: 500,
    borderRadius: 2,
    textTransform: 'none' as const,
    '&:hover': {
      background: darkMode ? 'rgba(30, 41, 59, 0.9)' : 'rgba(226, 232, 240, 0.9)',
      borderColor: colors.borderHover
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' } as any}
      PaperProps={{
        sx: {
          background: darkMode 
            ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.99) 100%)'
            : 'linear-gradient(180deg, #f8fafc 0%, #f0f4ff 100%)',
          backdropFilter: 'blur(20px)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderBottom: `1px solid ${colors.borderDefault}`,
        background: darkMode ? 'transparent' : 'rgba(255, 255, 255, 0.8)'
      }}>
        <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
          Add New Facility
        </Typography>
        <IconButton onClick={() => { telegram.hapticFeedback('light'); onClose(); }} sx={{ width: 48, height: 48, color: colors.textMuted }}>
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {/* Connection Status */}
          {!isOnline && (
            <Alert 
              severity="info" 
              icon={<CloudOffIcon />}
              sx={{ 
                backgroundColor: colors.warningBg,
                border: `1px solid ${colors.warningText}40`,
                color: colors.warningText
              }}
            >
              You're offline. Submission will be saved and uploaded when connected.
            </Alert>
          )}
          
          {/* Sync Status Indicator */}
          {(pendingCount > 0 || syncStatus === 'syncing' || lastSyncError) && (
            <Alert 
              severity={lastSyncError ? 'error' : syncStatus === 'syncing' ? 'info' : 'warning'}
              icon={
                syncStatus === 'syncing' ? <SyncIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} /> :
                lastSyncError ? <ErrorOutlineIcon /> :
                <CloudUploadIcon />
              }
              action={
                pendingCount > 0 && isOnline && syncStatus !== 'syncing' ? (
                  <Button 
                    color="inherit" 
                    size="small"
                    onClick={async () => {
                      setSyncStatus('syncing');
                      setLastSyncError(null);
                      console.log('🔄 Manual sync triggered');
                      try {
                        const result = await offlineQueue.syncPending();
                        console.log('📊 Sync result:', result);
                        if (result.failed > 0) {
                          setLastSyncError(`${result.failed} failed to sync`);
                          setSyncStatus('error');
                        } else {
                          setSyncStatus('idle');
                        }
                      } catch (err) {
                        console.error('❌ Manual sync failed:', err);
                        setLastSyncError(err instanceof Error ? err.message : 'Sync failed');
                        setSyncStatus('error');
                      }
                    }}
                  >
                    Retry Sync
                  </Button>
                ) : undefined
              }
              sx={{ 
                backgroundColor: lastSyncError ? colors.errorBg : syncStatus === 'syncing' ? colors.infoBg : colors.warningBg,
                border: `1px solid ${lastSyncError ? '#ef444440' : syncStatus === 'syncing' ? colors.neonBlue + '40' : colors.warningText + '40'}`,
                color: lastSyncError ? '#ef4444' : syncStatus === 'syncing' ? colors.neonBlue : colors.warningText
              }}
            >
              {syncStatus === 'syncing' ? (
                'Syncing submission to server...'
              ) : lastSyncError ? (
                `Sync error: ${lastSyncError}`
              ) : (
                `${pendingCount} pending submission${pendingCount > 1 ? 's' : ''} to sync`
              )}
            </Alert>
          )}

          {/* Success Message */}
          {submitStatus === 'success' && (
            <Alert 
              severity="success" 
              icon={<CheckCircleIcon />}
              sx={{ 
                backgroundColor: colors.successBg,
                border: `1px solid ${colors.neonGreen}50`,
                color: colors.successText
              }}
            >
              Facility added! Now visible on the map.
            </Alert>
          )}

          {submitStatus === 'queued' && (
            <Alert 
              severity="info" 
              icon={<CloudOffIcon />}
              sx={{ 
                backgroundColor: colors.infoBg,
                border: `1px solid ${colors.neonBlue}50`,
                color: colors.infoText
              }}
            >
              Saved! Will upload automatically when you're back online.
            </Alert>
          )}

          {/* Error Message */}
          {errorMessage && (
            <Alert severity="error" sx={{ backgroundColor: colors.errorBg }}>
              {errorMessage}
            </Alert>
          )}

          {/* Location Display */}
          <Box sx={{
            p: 2,
            background: colors.infoBg,
            borderRadius: 2,
            border: `1px solid ${colors.borderDefault}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon sx={{ color: colors.infoText }} />
              <Chip
                label={`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`}
                size="small"
                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, '_blank')}
                sx={{
                  backgroundColor: colors.warningBg,
                  color: colors.warningText,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '&:hover': { backgroundColor: darkMode ? 'rgba(251, 191, 36, 0.3)' : 'rgba(245, 158, 11, 0.3)' }
                }}
              />
            </Box>
          </Box>

          {/* API Number - At top of form (not shown for food vendors) */}
          {!isFoodVendor && (
          <Box>
            <TextField
              label="API Number *"
              value={apiNumber}
              onChange={(e) => {
                // Only allow numbers and dashes
                const filtered = e.target.value.replace(/[^0-9-]/g, '');
                setApiNumber(filtered);
              }}
              fullWidth
              placeholder="e.g., 30-015-12345-00-00"
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9-]*'
              }}
              InputLabelProps={{ sx: { color: colors.textMuted } }}
              InputProps={{
                endAdornment: isCheckingApi && <CyberSpinner size={20} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: colors.textPrimary,
                  '& fieldset': { 
                    borderColor: existingFacility ? `${colors.warningText}80` : colors.borderDefault
                  },
                  '&:hover fieldset': { 
                    borderColor: existingFacility ? colors.warningText : colors.borderHover
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: existingFacility ? colors.warningText : colors.borderFocus
                  }
                }
              }}
            />
            {/* API Found Indicator */}
            {existingFacility && (
              <Box
                onClick={() => {
                  if (onViewExistingFacility) {
                    telegram.hapticFeedback('light');
                    onViewExistingFacility(existingFacility);
                    onClose();
                  }
                }}
                sx={{
                  mt: 1,
                  p: 1.5,
                  backgroundColor: colors.warningBg,
                  border: `1px solid ${colors.warningText}50`,
                  borderRadius: 1,
                  cursor: onViewExistingFacility ? 'pointer' : 'default',
                  transition: 'all 0.2s ease',
                  '&:hover': onViewExistingFacility ? {
                    backgroundColor: darkMode ? 'rgba(251, 191, 36, 0.2)' : 'rgba(245, 158, 11, 0.25)',
                    transform: 'scale(1.01)'
                  } : {}
                }}
              >
                <Typography sx={{ 
                  color: colors.warningText, 
                  fontSize: '0.85rem', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  ⚠️ API Found
                </Typography>
                <Typography sx={{ 
                  color: colors.textPrimary, 
                  fontSize: '0.8rem',
                  mt: 0.5 
                }}>
                  {existingFacility.name}
                  {existingFacility.operator && (
                    <span style={{ color: colors.textMuted }}> • {existingFacility.operator}</span>
                  )}
                </Typography>
                {onViewExistingFacility && (
                  <Typography sx={{ 
                    color: colors.infoText, 
                    fontSize: '0.75rem',
                    mt: 0.5,
                    textDecoration: 'underline'
                  }}>
                    Click to view this facility →
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          )}

          {/* Facility Type */}
          <FormControl fullWidth>
            <InputLabel sx={{ color: colors.textMuted }}>Facility Type *</InputLabel>
            <Select
              value={facilityType}
              onChange={(e) => setFacilityType(e.target.value)}
              label="Facility Type *"
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: colors.bgCard,
                    '& .MuiMenuItem-root': {
                      color: colors.textPrimary,
                      '&:hover': { backgroundColor: colors.infoBg },
                      '&.Mui-selected': { backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.25)' : 'rgba(59, 130, 246, 0.2)' }
                    }
                  }
                }
              }}
              sx={{
                color: colors.textPrimary,
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.borderDefault
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.borderHover
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.borderFocus
                },
                '& .MuiSvgIcon-root': { color: colors.textMuted }
              }}
            >
              {FACILITY_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Name */}
          <TextField
            label={isFoodVendor ? "Business Name *" : "Facility Name *"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            placeholder={isFoodVendor ? "e.g., Taco El Güero, Maria's Kitchen" : "e.g., Smith Ranch Well #4"}
            InputLabelProps={{ sx: { color: colors.textMuted } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: colors.textPrimary,
                '& fieldset': { borderColor: colors.borderDefault },
                '&:hover fieldset': { borderColor: colors.borderHover },
                '&.Mui-focused fieldset': { borderColor: colors.borderFocus }
              }
            }}
          />

          {/* Operator - not shown for food vendors */}
          {!isFoodVendor && (
          <Autocomplete
            value={operator}
            onChange={(_, newValue) => setOperator(newValue || '')}
            inputValue={operator}
            onInputChange={(_, newInputValue) => setOperator(newInputValue)}
            options={operatorsList}
            freeSolo
            loading={isLoadingOperators}
            onFocus={() => fetchOperators()} // Lazy load operators when user focuses field
            renderInput={(params) => (
              <TextField
                {...params}
                label="Operator *"
                placeholder="Select or type operator name"
                InputLabelProps={{ sx: { color: colors.textMuted } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: colors.textPrimary,
                    '& fieldset': { borderColor: colors.borderDefault },
                    '&:hover fieldset': { borderColor: colors.borderHover },
                    '&.Mui-focused fieldset': { borderColor: colors.borderFocus }
                  },
                  '& .MuiAutocomplete-endAdornment': { '& .MuiSvgIcon-root': { color: colors.textMuted } }
                }}
              />
            )}
            ListboxProps={{
              sx: {
                backgroundColor: colors.bgCard,
                maxHeight: 300,
                '& .MuiAutocomplete-option': {
                  color: colors.textPrimary,
                  '&:hover': { backgroundColor: colors.infoBg },
                  '&[aria-selected=\"true\"]': { backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.25)' : 'rgba(59, 130, 246, 0.2)' }
                }
              }
            }}
          />
          )}

          {/* County */}
          <FormControl fullWidth>
            <InputLabel sx={{ color: colors.textMuted }}>County *</InputLabel>
            <Select
              value={county}
              onChange={(e) => setCounty(e.target.value)}
              label="County *"
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: colors.bgCard,
                    maxHeight: 300,
                    '& .MuiMenuItem-root': {
                      color: colors.textPrimary,
                      '&:hover': { backgroundColor: colors.infoBg },
                      '&.Mui-selected': { backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.25)' : 'rgba(59, 130, 246, 0.2)' }
                    }
                  }
                }
              }}
              sx={{
                color: colors.textPrimary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.borderDefault },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.borderHover },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.borderFocus },
                '& .MuiSvgIcon-root': { color: colors.textMuted }
              }}
            >
              <MenuItem value="" disabled>
                <em>Select a county</em>
              </MenuItem>
              {NEW_MEXICO_COUNTIES.map((c) => (
                <MenuItem key={c.name} value={c.name}>
                  {c.name} ({c.basin})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Notes */}
          <TextField
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Any helpful details for finding this location..."
            InputLabelProps={{ sx: { color: colors.textMuted } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: colors.textPrimary,
                '& fieldset': { borderColor: colors.borderDefault },
                '&:hover fieldset': { borderColor: colors.borderHover },
                '&.Mui-focused fieldset': { borderColor: colors.borderFocus }
              }
            }}
          />

          {/* ========== Food Vendor Business Verification Fields ========== */}
          {isFoodVendor && (
            <Box sx={{
              p: 2,
              background: darkMode 
                ? 'linear-gradient(135deg, rgba(251, 146, 60, 0.1) 0%, rgba(234, 88, 12, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(234, 88, 12, 0.1) 100%)',
              borderRadius: 2,
              border: `1px solid ${darkMode ? 'rgba(251, 146, 60, 0.3)' : 'rgba(234, 88, 12, 0.4)'}`,
            }}>
              <Typography sx={{ 
                color: darkMode ? '#fb923c' : '#ea580c', 
                fontWeight: 700, 
                fontSize: '1rem',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                🍽️ Food Vendor Business Verification
              </Typography>
              <Typography sx={{ 
                color: colors.textMuted, 
                fontSize: '0.8rem',
                mb: 2,
                fontStyle: 'italic'
              }}>
                Please provide your business credentials to verify you're a licensed food vendor. This helps oilfield workers trust your business!
              </Typography>

              <Stack spacing={2}>
                {/* Business License */}
                <TextField
                  label="Business License Number *"
                  value={businessLicense}
                  onChange={(e) => setBusinessLicense(e.target.value)}
                  fullWidth
                  placeholder="e.g., BL-2024-12345"
                  InputLabelProps={{ sx: { color: colors.textMuted } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: colors.textPrimary,
                      '& fieldset': { borderColor: colors.borderDefault },
                      '&:hover fieldset': { borderColor: colors.borderHover },
                      '&.Mui-focused fieldset': { borderColor: '#fb923c' }
                    }
                  }}
                />

                {/* EIN */}
                <TextField
                  label="EIN (Employer ID Number)"
                  value={ein}
                  onChange={(e) => {
                    // Format as XX-XXXXXXX
                    const cleaned = e.target.value.replace(/[^0-9]/g, '');
                    if (cleaned.length <= 9) {
                      const formatted = cleaned.length > 2 
                        ? `${cleaned.slice(0, 2)}-${cleaned.slice(2)}` 
                        : cleaned;
                      setEin(formatted);
                    }
                  }}
                  fullWidth
                  placeholder="e.g., 12-3456789"
                  helperText="Optional - 9-digit federal tax ID"
                  inputProps={{ inputMode: 'numeric' }}
                  InputLabelProps={{ sx: { color: colors.textMuted } }}
                  FormHelperTextProps={{ sx: { color: colors.textMuted } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: colors.textPrimary,
                      '& fieldset': { borderColor: colors.borderDefault },
                      '&:hover fieldset': { borderColor: colors.borderHover },
                      '&.Mui-focused fieldset': { borderColor: '#fb923c' }
                    }
                  }}
                />

                {/* Food Handler's Permit */}
                <TextField
                  label="Food Handler's Permit # *"
                  value={foodPermit}
                  onChange={(e) => setFoodPermit(e.target.value)}
                  fullWidth
                  placeholder="e.g., FH-NM-2024-5678"
                  helperText="State/county food handler certification"
                  InputLabelProps={{ sx: { color: colors.textMuted } }}
                  FormHelperTextProps={{ sx: { color: colors.textMuted } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: colors.textPrimary,
                      '& fieldset': { borderColor: colors.borderDefault },
                      '&:hover fieldset': { borderColor: colors.borderHover },
                      '&.Mui-focused fieldset': { borderColor: '#fb923c' }
                    }
                  }}
                />

                {/* Health Department Permit */}
                <TextField
                  label="Health Department Permit #"
                  value={healthPermit}
                  onChange={(e) => setHealthPermit(e.target.value)}
                  fullWidth
                  placeholder="e.g., HD-LEA-2024-001"
                  helperText="County health department inspection certificate"
                  InputLabelProps={{ sx: { color: colors.textMuted } }}
                  FormHelperTextProps={{ sx: { color: colors.textMuted } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: colors.textPrimary,
                      '& fieldset': { borderColor: colors.borderDefault },
                      '&:hover fieldset': { borderColor: colors.borderHover },
                      '&.Mui-focused fieldset': { borderColor: '#fb923c' }
                    }
                  }}
                />

                {/* Menu Specialty */}
                <TextField
                  label="Menu Specialty *"
                  value={menuSpecialty}
                  onChange={(e) => setMenuSpecialty(e.target.value)}
                  fullWidth
                  placeholder="e.g., Tacos, Burritos, Breakfast"
                  helperText="What food do you serve?"
                  InputLabelProps={{ sx: { color: colors.textMuted } }}
                  FormHelperTextProps={{ sx: { color: colors.textMuted } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: colors.textPrimary,
                      '& fieldset': { borderColor: colors.borderDefault },
                      '&:hover fieldset': { borderColor: colors.borderHover },
                      '&.Mui-focused fieldset': { borderColor: '#fb923c' }
                    }
                  }}
                />

                {/* Operating Hours */}
                <TextField
                  label="Operating Hours *"
                  value={operatingHours}
                  onChange={(e) => setOperatingHours(e.target.value)}
                  fullWidth
                  placeholder="e.g., Mon-Fri 6am-2pm, Sat 7am-12pm"
                  helperText="When are you typically at this location?"
                  InputLabelProps={{ sx: { color: colors.textMuted } }}
                  FormHelperTextProps={{ sx: { color: colors.textMuted } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: colors.textPrimary,
                      '& fieldset': { borderColor: colors.borderDefault },
                      '&:hover fieldset': { borderColor: colors.borderHover },
                      '&.Mui-focused fieldset': { borderColor: '#fb923c' }
                    }
                  }}
                />

                {/* Contact Phone */}
                <TextField
                  label="Contact Phone *"
                  value={contactPhone}
                  onChange={(e) => {
                    // Format phone number
                    const cleaned = e.target.value.replace(/[^0-9]/g, '');
                    if (cleaned.length <= 10) {
                      let formatted = cleaned;
                      if (cleaned.length >= 6) {
                        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
                      } else if (cleaned.length >= 3) {
                        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
                      }
                      setContactPhone(formatted);
                    }
                  }}
                  fullWidth
                  placeholder="(575) 555-1234"
                  helperText="For customers to call ahead or place orders"
                  inputProps={{ inputMode: 'tel' }}
                  InputLabelProps={{ sx: { color: colors.textMuted } }}
                  FormHelperTextProps={{ sx: { color: colors.textMuted } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: colors.textPrimary,
                      '& fieldset': { borderColor: colors.borderDefault },
                      '&:hover fieldset': { borderColor: colors.borderHover },
                      '&.Mui-focused fieldset': { borderColor: '#fb923c' }
                    }
                  }}
                />

                {/* Payment Methods */}
                <Box>
                  <Typography sx={{ color: colors.textMuted, fontSize: '0.875rem', mb: 1 }}>
                    Payment Methods Accepted
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label="💵 Cash"
                      onClick={() => setAcceptsCash(!acceptsCash)}
                      sx={{
                        backgroundColor: acceptsCash 
                          ? (darkMode ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.2)')
                          : colors.bgSecondary,
                        color: acceptsCash ? colors.neonGreen : colors.textMuted,
                        border: `1px solid ${acceptsCash ? colors.neonGreen : colors.borderDefault}`,
                        cursor: 'pointer',
                        fontWeight: acceptsCash ? 600 : 400,
                        '&:hover': { backgroundColor: darkMode ? 'rgba(34, 197, 94, 0.4)' : 'rgba(34, 197, 94, 0.3)' }
                      }}
                    />
                    <Chip
                      label="💳 Card"
                      onClick={() => setAcceptsCards(!acceptsCards)}
                      sx={{
                        backgroundColor: acceptsCards 
                          ? (darkMode ? 'rgba(96, 165, 250, 0.3)' : 'rgba(96, 165, 250, 0.2)')
                          : colors.bgSecondary,
                        color: acceptsCards ? colors.neonBlue : colors.textMuted,
                        border: `1px solid ${acceptsCards ? colors.neonBlue : colors.borderDefault}`,
                        cursor: 'pointer',
                        fontWeight: acceptsCards ? 600 : 400,
                        '&:hover': { backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.4)' : 'rgba(96, 165, 250, 0.3)' }
                      }}
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Photo Section */}
          <Box>
            <Typography sx={{ color: colors.textMuted, mb: 1.5, fontSize: '0.875rem' }}>
              {isFoodVendor ? '📍 Location Photo *' : 'Photo (Optional but recommended)'}
            </Typography>
            {isFoodVendor && (
              <Typography sx={{ color: colors.textMuted, mb: 1, fontSize: '0.75rem', fontStyle: 'italic' }}>
                Show customers where to find you! Take a photo of your truck/stand at this location.
              </Typography>
            )}

            {!photo ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Camera Button */}
                <Button
                  variant="outlined"
                  startIcon={<CameraAltIcon />}
                  onClick={() => cameraInputRef.current?.click()}
                  sx={isFoodVendor ? {
                    ...secondaryButtonSx,
                    borderColor: '#fb923c',
                    color: '#fb923c',
                    '&:hover': { borderColor: '#ea580c', backgroundColor: 'rgba(251, 146, 60, 0.1)' }
                  } : secondaryButtonSx}
                >
                  Take Photo
                </Button>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoSelect}
                  style={{ display: 'none' }}
                />

                {/* Gallery Button */}
                <Button
                  variant="outlined"
                  startIcon={<PhotoLibraryIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={secondaryButtonSx}
                >
                  Choose Photo
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  style={{ display: 'none' }}
                />
              </Box>
            ) : (
              <Box sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${colors.borderDefault}`
              }}>
                {photo.isCompressing ? (
                  <Box sx={{
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: colors.bgSecondary
                  }}>
                    <CyberSpinner size={40} />
                  </Box>
                ) : (
                  <>
                    <img
                      src={photo.compressed?.base64}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: 300,
                        objectFit: 'cover'
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: 1,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Chip
                        label={formatFileSize(photo.compressed?.size || 0)}
                        size="small"
                        sx={{
                          backgroundColor: colors.successBg,
                          color: colors.successText,
                          fontSize: '0.75rem'
                        }}
                      />
                      <IconButton
                        onClick={handleRemovePhoto}
                        sx={{ width: 44, height: 44, color: '#ef4444' }}
                      >
                        <DeleteIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* ========== Menu Photos Section (Food Vendors Only) ========== */}
          {isFoodVendor && (
            <Box sx={{
              p: 2,
              background: darkMode 
                ? 'linear-gradient(135deg, rgba(234, 88, 12, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(234, 88, 12, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)',
              borderRadius: 2,
              border: `1px solid ${darkMode ? 'rgba(234, 88, 12, 0.3)' : 'rgba(234, 88, 12, 0.4)'}`,
            }}>
              <Typography sx={{ 
                color: darkMode ? '#fb923c' : '#ea580c', 
                fontWeight: 700, 
                fontSize: '1rem',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                📸 Menu Photos ({menuPhotos.length}/{MAX_MENU_PHOTOS})
              </Typography>
              <Typography sx={{ 
                color: colors.textMuted, 
                fontSize: '0.8rem',
                mb: 2,
                fontStyle: 'italic'
              }}>
                Upload photos of your menu, food items, or price board. Workers love seeing what's available!
              </Typography>

              {/* Menu Photo Grid */}
              {menuPhotos.length > 0 && (
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', 
                  gap: 1, 
                  mb: 2 
                }}>
                  {menuPhotos.map((menuPhoto, index) => (
                    <Box key={index} sx={{
                      position: 'relative',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: `1px solid ${colors.borderDefault}`,
                      aspectRatio: '1',
                    }}>
                      {menuPhoto.isCompressing ? (
                        <Box sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: colors.bgSecondary
                        }}>
                          <CyberSpinner size={24} />
                        </Box>
                      ) : (
                        <>
                          <img
                            src={menuPhoto.compressed?.base64}
                            alt={`Menu ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          <IconButton
                            onClick={() => {
                              setMenuPhotos(prev => prev.filter((_, i) => i !== index));
                            }}
                            sx={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              width: 28,
                              height: 28,
                              backgroundColor: 'rgba(239, 68, 68, 0.9)',
                              color: '#fff',
                              '&:hover': { backgroundColor: '#dc2626' }
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Add Menu Photo Button */}
              {menuPhotos.length < MAX_MENU_PHOTOS && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CameraAltIcon />}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.capture = 'environment';
                      input.onchange = async (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (!file) return;
                        
                        const newPhoto: PhotoPreview = {
                          original: file,
                          compressed: null,
                          isCompressing: true
                        };
                        setMenuPhotos(prev => [...prev, newPhoto]);
                        
                        try {
                          const result = await compressImage(file, {
                            maxWidth: 1200,
                            maxHeight: 1200,
                            quality: 0.8
                          });
                          setMenuPhotos(prev => prev.map((p, i) => 
                            i === prev.length - 1 ? {
                              ...p,
                              compressed: { base64: result.base64, size: result.compressedSize },
                              isCompressing: false
                            } : p
                          ));
                        } catch (error) {
                          console.error('Failed to compress menu photo:', error);
                          setMenuPhotos(prev => prev.slice(0, -1));
                          setErrorMessage('Failed to process photo. Please try again.');
                        }
                      };
                      input.click();
                    }}
                    sx={{
                      borderColor: '#fb923c',
                      color: '#fb923c',
                      '&:hover': { borderColor: '#ea580c', backgroundColor: 'rgba(251, 146, 60, 0.1)' }
                    }}
                  >
                    📷 Take Menu Photo
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoLibraryIcon />}
                    onClick={() => menuInputRef.current?.click()}
                    sx={{
                      borderColor: colors.borderDefault,
                      color: colors.textMuted,
                      '&:hover': { borderColor: colors.borderHover }
                    }}
                  >
                    Choose
                  </Button>
                  <input
                    ref={menuInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      const remaining = MAX_MENU_PHOTOS - menuPhotos.length;
                      const filesToProcess = files.slice(0, remaining);
                      
                      for (const file of filesToProcess) {
                        const newPhoto: PhotoPreview = {
                          original: file,
                          compressed: null,
                          isCompressing: true
                        };
                        setMenuPhotos(prev => [...prev, newPhoto]);
                        
                        try {
                          const result = await compressImage(file, {
                            maxWidth: 1200,
                            maxHeight: 1200,
                            quality: 0.8
                          });
                          setMenuPhotos(prev => {
                            const idx = prev.findIndex(p => p.original === file);
                            if (idx === -1) return prev;
                            return prev.map((p, i) => 
                              i === idx ? {
                                ...p,
                                compressed: { base64: result.base64, size: result.compressedSize },
                                isCompressing: false
                              } : p
                            );
                          });
                        } catch (error) {
                          console.error('Failed to compress menu photo:', error);
                          setMenuPhotos(prev => prev.filter(p => p.original !== file));
                        }
                      }
                      if (menuInputRef.current) menuInputRef.current.value = '';
                    }}
                    style={{ display: 'none' }}
                  />
                </Box>
              )}

              {menuPhotos.length >= MAX_MENU_PHOTOS && (
                <Typography sx={{ color: colors.warningText, fontSize: '0.8rem' }}>
                  ✓ Maximum {MAX_MENU_PHOTOS} menu photos reached
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </Box>

      {/* Footer */}
      <Box sx={{
        p: 3,
        borderTop: `1px solid ${colors.borderDefault}`,
        background: darkMode ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.95)'
      }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !facilityType || !name || submitStatus !== 'idle'}
          startIcon={isSubmitting ? <CyberSpinner size={20} /> : 
            (isOnline ? <CloudUploadIcon /> : <CloudOffIcon />)}
          sx={primaryButtonSx}
        >
          {isSubmitting ? 'Adding...' : 
            (isOnline ? 'Add Facility' : 'Save for Later Upload')}
        </Button>
      </Box>
    </Dialog>

    {/* Share Dialog - appears after successful submission */}
    <Dialog 
      open={showShareDialog} 
      onClose={closeShareDialog}
      PaperProps={{
        sx: {
          background: colors.bgCard,
          border: `2px solid ${colors.neonGreen}`,
          borderRadius: 3,
          minWidth: { xs: '90%', sm: 360 }
        }
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ 
            width: 60, 
            height: 60, 
            borderRadius: '50%', 
            background: `${colors.neonGreen}30`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}>
            <Typography sx={{ fontSize: 32 }}>✅</Typography>
          </Box>
          <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
            Facility Added!
          </Typography>
          <Typography sx={{ color: colors.textMuted, mt: 1 }}>
            Your facility is now visible on the map. Share it with your crew?
          </Typography>
        </Box>

        {lastSubmittedFacility && (
          <Box sx={{ 
            background: colors.bgSecondary, 
            borderRadius: 2, 
            p: 2, 
            mb: 3,
            border: `1px solid ${colors.borderDefault}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocationOnIcon sx={{ color: colors.neonBlue, fontSize: 20 }} />
              <Typography sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                {FACILITY_TYPES.find(f => f.value === lastSubmittedFacility.facilityType)?.label || 'Facility'}
              </Typography>
            </Box>
            <Typography sx={{ color: colors.textPrimary, fontSize: '1rem', fontWeight: 600 }}>
              {lastSubmittedFacility.name}
            </Typography>
            {lastSubmittedFacility.operator && (
              <Typography sx={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                {lastSubmittedFacility.operator}
              </Typography>
            )}
            <Chip
              label={`📍 ${lastSubmittedFacility.latitude.toFixed(5)}, ${lastSubmittedFacility.longitude.toFixed(5)}`}
              size="small"
              onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${lastSubmittedFacility.latitude},${lastSubmittedFacility.longitude}`, '_blank')}
              sx={{
                mt: 1,
                backgroundColor: colors.warningBg,
                color: colors.warningText,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.75rem',
                '&:hover': { backgroundColor: darkMode ? 'rgba(251, 191, 36, 0.3)' : 'rgba(245, 158, 11, 0.3)' }
              }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<LocationOnIcon />}
            onClick={() => {
              if (lastSubmittedFacility && onViewOnMap) {
                telegram.hapticFeedback('light');
                closeShareDialog();
                onViewOnMap(lastSubmittedFacility.latitude, lastSubmittedFacility.longitude, lastSubmittedFacility.name);
              }
            }}
            fullWidth
            sx={{ 
              background: `linear-gradient(135deg, ${colors.neonGreen} 0%, #059669 100%)`,
              color: '#fff',
              fontWeight: 600,
              py: 1.5,
              '&:hover': {
                background: `linear-gradient(135deg, #059669 0%, ${colors.neonGreen} 100%)`,
              }
            }}
          >
            View on Map
          </Button>
          <Button
            variant="contained"
            startIcon={<ShareIcon />}
            onClick={handleShare}
            fullWidth
            sx={{ 
              background: `linear-gradient(135deg, ${colors.neonBlue} 0%, ${colors.neonCyan} 100%)`,
              color: '#fff',
              fontWeight: 600,
              py: 1.5,
              '&:hover': {
                background: `linear-gradient(135deg, ${colors.neonCyan} 0%, ${colors.neonBlue} 100%)`,
              }
            }}
          >
            Share Location
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyLink}
            fullWidth
            sx={{ 
              borderColor: colors.borderDefault, 
              color: colors.textPrimary,
              '&:hover': {
                borderColor: colors.neonBlue,
                background: `${colors.neonBlue}10`
              }
            }}
          >
            Copy Link
          </Button>
          <Button
            variant="outlined"
            startIcon={<ListAltIcon />}
            onClick={() => {
              telegram.hapticFeedback('light');
              closeShareDialog();
              if (onOpenCommunityFacilities) {
                onOpenCommunityFacilities();
              }
            }}
            fullWidth
            sx={{ 
              borderColor: colors.neonPurple, 
              color: colors.neonPurple,
              '&:hover': {
                borderColor: colors.neonPurple,
                background: `${colors.neonPurple}15`
              }
            }}
          >
            View All Community Facilities
          </Button>
          <Button 
            onClick={closeShareDialog}
            fullWidth
            sx={{ color: colors.textMuted }}
          >
            Done
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
    </>
  );
}
